import { FulfillmentGroupStatus } from "@redotech/redo-fulfillment-common/fulfillment-group-status";
import { AdvancedFilterData } from "@redotech/redo-views-common/advanced-filters/advanced-filter-data";
import { ArrayStringFilterOperator } from "@redotech/redo-views-common/advanced-filters/array-string-filter";
import { AdvancedFilterType } from "@redotech/redo-views-common/advanced-filters/generic-advanced-filter-data";
import { SortDirection, TableSort } from "@redotech/redo-views-common/table";
import { DEFAULT_PAGE_SIZE, viewTypes } from "./views";

export enum OutboundSystemView {
  AWAITING_SHIPMENT = "awaiting-shipment",
  SHIPMENTS = "shipments",
  INTERNATIONAL = "international",
  PRIORITY = "priority",
  IN_STORE_PICKUP = "in-store-pickup",
  CREATE = "create",
}

export const toTitleCase = (str: string) => {
  return str
    .trim()
    .replace(/\w\S*/g, (txt) =>
      txt.length > 2
        ? txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase()
        : txt,
    );
};

export const sortDefault: TableSort = {
  direction: SortDirection.DESC,
  key: "createdAt",
};

const completeFilter: AdvancedFilterData = {
  name: "status",
  type: AdvancedFilterType.ARRAY_TO_STRING,
  value: [toTitleCase(FulfillmentGroupStatus.Closed)],
  operator: ArrayStringFilterOperator.ANY_OF,
};

const incompleteFilter: AdvancedFilterData = {
  name: "status",
  type: AdvancedFilterType.ARRAY_TO_STRING,
  value: [toTitleCase(FulfillmentGroupStatus.Open)],
  operator: ArrayStringFilterOperator.ANY_OF,
};

const shippingFilter: AdvancedFilterData = {
  name: "deliveryMethod",
  type: AdvancedFilterType.ARRAY_TO_STRING,
  value: ["SHIPPING"],
  operator: ArrayStringFilterOperator.ANY_OF,
};

const inStorePickupFilter: AdvancedFilterData = {
  name: "deliveryMethod",
  type: AdvancedFilterType.ARRAY_TO_STRING,
  value: ["PICK_UP"],
  operator: ArrayStringFilterOperator.ANY_OF,
};

const notPickedUpFilter: AdvancedFilterData = {
  name: "status",
  type: AdvancedFilterType.ARRAY_TO_STRING,
  value: [
    toTitleCase(FulfillmentGroupStatus.Open),
    toTitleCase(FulfillmentGroupStatus.ReadyForPickup),
  ],
  operator: ArrayStringFilterOperator.ANY_OF,
};

const internationalFilter: AdvancedFilterData = {
  name: "countries",
  type: AdvancedFilterType.ARRAY_TO_STRING,
  value: ["US"],
  operator: ArrayStringFilterOperator.NONE_OF,
};

export const FAST_PASS_TAG = "redo-fast-pass";
export const FAST_PASS_VIEW_NAME = "Fast Pass";

const fastPassFilter: AdvancedFilterData = {
  name: "shopifyOrderTags",
  type: AdvancedFilterType.ARRAY_TO_STRING,
  value: [FAST_PASS_TAG],
  operator: ArrayStringFilterOperator.ANY_OF,
};

// These must match the column definitions found in the table, else "Save as
const defaultColumns = [
  { key: "selected", width: 24, hidden: false },
  { key: "is-packing-slip-printed", width: 120, hidden: false },
  { key: "is-label-printed", width: 120, hidden: true },
  { key: "is-pick-list-printed", width: 120, hidden: true },
  { key: "status", width: 100, hidden: false },
  { key: "delivery-method", width: 120, hidden: true },
  { key: "address-verification", width: 160, hidden: false },
  { key: "fraud-risk", width: 120, hidden: true },
  { key: "tag", width: 150, hidden: true },
  { key: "order-name", width: 120, hidden: false },
  { key: "customer", width: 160, hidden: false },
  { key: "order-date", width: 120, hidden: false },
  { key: "order-age", width: 100, hidden: true },
  { key: "shipped-date", width: 120, hidden: true },
  { key: "item-name", width: 80, hidden: true },
  { key: "item-sku", width: 80, hidden: false },
  { key: "product-variant", width: 120, hidden: true },
  { key: "total", width: 100, hidden: false },
  { key: "quantity", width: 80, hidden: false },
  { key: "total-sku-count", width: 80, hidden: true },
  { key: "weight", width: 80, hidden: false },
  { key: "parcel-selection", width: 160, hidden: false },
  { key: "shippingSelection", width: 320, hidden: false },
  { key: "insured", width: 120, hidden: true },
  { key: "estimated-delivery-date", width: 120, hidden: true },
  { key: "trackingNumber", width: 120, hidden: true },
  { key: "rate", width: 80, hidden: true },
  { key: "shippingPaid", width: 120, hidden: true },
  { key: "shippingMargin", width: 120, hidden: true },
  { key: "requested-service", width: 160, hidden: false },
  { key: "shipment-created-at", width: 120, hidden: true },
  { key: "delivery-status", width: 120, hidden: true },
  { key: "shopify-order-tags", width: 120, hidden: true },
  { key: "state", width: 80, hidden: true },
  { key: "destination-country", width: 80, hidden: false },
  { key: "postal-code", width: 80, hidden: true },
  { key: "fulfillment-location", width: 120, hidden: false },
  { key: "note-from-buyer", width: 310, hidden: true },
  { key: "note-to-buyer", width: 310, hidden: true },
  { key: "internal-notes", width: 310, hidden: true },
  { key: "fulfilled-by", width: 120, hidden: true },
  { key: "scan-verified", width: 120, hidden: true },
  { key: "assigned-user", width: 120, hidden: true },
  { key: "batch-id", width: 120, hidden: true },
  { key: "stores", width: 120, hidden: true },
  { key: "vendors", width: 120, hidden: true },
  { key: "bin", width: 80, hidden: true },
  { key: "electronic-com-invoice", width: 120, hidden: true },
  { key: "usps-zone", width: 80, hidden: true },
];

const shippingColumns = [
  { key: "selected", width: 24, hidden: false },
  { key: "order-name", width: 120, hidden: false },
  { key: "trackingNumber", width: 120, hidden: false },
  { key: "delivery-status", width: 120, hidden: false },
  { key: "estimated-delivery-date", width: 120, hidden: false },
  { key: "customer", width: 160, hidden: false },
  { key: "order-date", width: 120, hidden: false },
  { key: "shipped-date", width: 120, hidden: false },
  { key: "shippingSelection", width: 320, hidden: false },
  { key: "assigned-user", width: 120, hidden: false },
];

export const FastPassView: viewTypes["fulfillment-groups"] = {
  table: "fulfillment-groups",
  name: FAST_PASS_VIEW_NAME,
  filters: [incompleteFilter, fastPassFilter],
  columns: defaultColumns,
  pageSize: DEFAULT_PAGE_SIZE,
  sort: sortDefault,
  position: 5,
};

export const StaticSystemViews: Record<
  OutboundSystemView,
  viewTypes["fulfillment-groups"]
> = {
  [OutboundSystemView.AWAITING_SHIPMENT]: {
    table: "fulfillment-groups",
    name: OutboundSystemView.AWAITING_SHIPMENT,
    filters: [incompleteFilter, shippingFilter],
    columns: defaultColumns,
    pageSize: DEFAULT_PAGE_SIZE,
    sort: sortDefault,
    position: 0,
  },
  [OutboundSystemView.SHIPMENTS]: {
    table: "fulfillment-groups",
    name: OutboundSystemView.SHIPMENTS,
    filters: [completeFilter, shippingFilter],
    columns: shippingColumns,
    pageSize: DEFAULT_PAGE_SIZE,
    sort: sortDefault,
    position: 1,
  },
  [OutboundSystemView.INTERNATIONAL]: {
    table: "fulfillment-groups",
    name: OutboundSystemView.INTERNATIONAL,
    filters: [internationalFilter],
    columns: defaultColumns,
    pageSize: DEFAULT_PAGE_SIZE,
    sort: sortDefault,
    position: 2,
  },
  [OutboundSystemView.PRIORITY]: {
    table: "fulfillment-groups",
    name: OutboundSystemView.PRIORITY,
    filters: [],
    columns: defaultColumns,
    pageSize: DEFAULT_PAGE_SIZE,
    sort: sortDefault,
    position: 3,
  },
  [OutboundSystemView.IN_STORE_PICKUP]: {
    table: "fulfillment-groups",
    name: OutboundSystemView.IN_STORE_PICKUP,
    filters: [notPickedUpFilter, inStorePickupFilter],
    columns: defaultColumns,
    pageSize: DEFAULT_PAGE_SIZE,
    sort: sortDefault,
    position: 4,
  },
  [OutboundSystemView.CREATE]: {
    table: "fulfillment-groups",
    name: OutboundSystemView.CREATE,
    filters: [],
    columns: defaultColumns,
    pageSize: DEFAULT_PAGE_SIZE,
    sort: sortDefault,
    position: -1,
  },
};

export function friendlyViewName(
  view: Pick<viewTypes["purchase-orders"], "name">,
) {
  if (view.name === OutboundSystemView.AWAITING_SHIPMENT) {
    return "Ready to ship";
  }
  if (view.name === OutboundSystemView.SHIPMENTS) {
    return "Shipments";
  }
  if (view.name === "bulk-fulfillments") {
    return "Bulk shipments";
  }

  if (view.name === OutboundSystemView.INTERNATIONAL) {
    return "International";
  }
  if (view.name === OutboundSystemView.PRIORITY) {
    return "Priority";
  }
  if (view.name === OutboundSystemView.IN_STORE_PICKUP) {
    return "In-store pickup";
  }

  return view.name;
}
